import React, { useState } from 'react';
import { 
  Button, 
  Card, 
  Tabs, 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Switch, 
  Table, 
  Space, 
  Breadcrumb, 
  message, 
  Row, 
  Col,
  Divider,
  Typography,
  Modal,
  DatePicker,
  Radio,
  Tooltip,
  Tag,
  Alert,
  Slider
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined,
  QuestionCircleOutlined,
  CalendarOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// 添加业务类型选项常量
const BUSINESS_TYPE_OPTIONS = [
  { value: '住宿', label: '住宿' },
  { value: '餐饮', label: '餐饮' },
  { value: '会员充值', label: '会员充值' },
  { value: 'SPA服务', label: 'SPA服务' },
  { value: '会议服务', label: '会议服务' },
  { value: '商品零售', label: '商品零售' },
  { value: '其他服务', label: '其他服务' },
];

// 添加特殊场景类型选项
const SPECIAL_SCENARIO_TYPES = [
  { value: '节假日', label: '节假日特殊定价' },
  { value: '促销活动', label: '促销活动期间' },
  { value: '竞争加价', label: '竞争对手价格变化' },
  { value: '高需求期', label: '高需求季节/时段' },
  { value: '会员专属', label: '会员专属活动' },
  { value: '新客专享', label: '新客户专享' },
  { value: '复购奖励', label: '客户复购奖励' },
];

// 添加应用条件选项
const CONDITION_OPTIONS = [
  { value: 'all_orders', label: '所有符合条件的订单' },
  { value: 'new_customers', label: '仅适用于新客户' },
  { value: 'repeat_customers', label: '仅适用于复购客户' },
  { value: 'vip_customers', label: '仅适用于会员客户' },
];

interface CommissionRuleData {
  key: string;
  type: string;
  rate: number;
  minAmount?: number;
  maxAmount?: number;
  validityType: 'date-range' | 'long-term';
  validDateRange?: [string, string];
  validDays?: number;
  description: string;
  status: boolean;
}

interface LadderRuleData {
  key: string;
  level: string;
  salesMin: number;
  salesMax: number;
  bonusRate: number;
  status: boolean;
}

interface SpecialScenarioData {
  key: string;
  scenarioName: string;
  scenarioType: string;
  commissionRate: number;
  validityType: 'date-range' | 'long-term';
  validDateRange?: [string, string];
  validPeriod?: number;
  priority: number;
  applyCondition: string;
  status: boolean;
}

const CommissionConfigManagement: React.FC = () => {
  const [basicRulesForm] = Form.useForm();
  const [addRuleForm] = Form.useForm();
  const [validityForm] = Form.useForm();
  const [addScenarioForm] = Form.useForm();
  const [editingKey, setEditingKey] = useState<string>('');
  const [addRuleModalVisible, setAddRuleModalVisible] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [addScenarioModalVisible, setAddScenarioModalVisible] = useState<boolean>(false);
  const [scenarioConfirmLoading, setScenarioConfirmLoading] = useState<boolean>(false);
  
  // 基础分佣规则示例数据
  const basicRules: CommissionRuleData[] = [
    {
      key: '1',
      type: '住宿',
      rate: 5,
      minAmount: 500,
      maxAmount: 10000,
      validityType: 'date-range',
      validDateRange: [dayjs().format('YYYY-MM-DD'), dayjs().add(30, 'day').format('YYYY-MM-DD')],
      description: '客房预订及相关服务',
      status: true,
    },
    {
      key: '2',
      type: '餐饮',
      rate: 3,
      minAmount: 100,
      maxAmount: 5000,
      validityType: 'long-term',
      description: '餐厅消费',
      status: true,
    },
    {
      key: '3',
      type: '会员充值',
      rate: 2,
      minAmount: 1000,
      validityType: 'date-range',
      validDateRange: [dayjs().format('YYYY-MM-DD'), dayjs().add(60, 'day').format('YYYY-MM-DD')],
      description: '会员卡充值',
      status: true,
    },
  ];
  
  // 阶梯奖励规则示例数据
  const ladderRules: LadderRuleData[] = [
    {
      key: '1',
      level: '铜牌',
      salesMin: 0,
      salesMax: 10000,
      bonusRate: 0.5,
      status: true,
    },
    {
      key: '2',
      level: '银牌',
      salesMin: 10001,
      salesMax: 50000,
      bonusRate: 1,
      status: true,
    },
    {
      key: '3',
      level: '金牌',
      salesMin: 50001,
      salesMax: 100000,
      bonusRate: 1.5,
      status: true,
    },
    {
      key: '4',
      level: '钻石',
      salesMin: 100001,
      salesMax: 99999999,
      bonusRate: 2,
      status: true,
    },
  ];
  
  // 特殊场景分佣示例数据 - 更新以匹配新的接口
  const specialScenarios: SpecialScenarioData[] = [
    {
      key: '1',
      scenarioName: '节假日客房',
      scenarioType: '节假日',
      commissionRate: 8,
      validityType: 'date-range',
      validDateRange: [dayjs().format('YYYY-MM-DD'), dayjs().add(15, 'day').format('YYYY-MM-DD')],
      validPeriod: 15,
      priority: 10,
      applyCondition: 'all_orders',
      status: true,
    },
    {
      key: '2',
      scenarioName: '生日宴会',
      scenarioType: '促销活动',
      commissionRate: 5,
      validityType: 'date-range',
      validDateRange: [dayjs().format('YYYY-MM-DD'), dayjs().add(3, 'day').format('YYYY-MM-DD')],
      validPeriod: 3,
      priority: 8,
      applyCondition: 'all_orders',
      status: true,
    },
    {
      key: '3',
      scenarioName: '超级会员日',
      scenarioType: '会员专属',
      commissionRate: 3,
      validityType: 'date-range',
      validDateRange: [dayjs().format('YYYY-MM-DD'), dayjs().add(1, 'day').format('YYYY-MM-DD')],
      validPeriod: 1,
      priority: 9,
      applyCondition: 'vip_customers',
      status: true,
    },
  ];
  
  const [basicRulesList, setBasicRulesList] = useState<CommissionRuleData[]>(basicRules);
  const [ladderRulesList, setLadderRulesList] = useState<LadderRuleData[]>(ladderRules);
  const [specialScenariosList, setSpecialScenariosList] = useState<SpecialScenarioData[]>(specialScenarios);

  // 基础分佣规则表格列
  const basicRulesColumns: ColumnsType<CommissionRuleData> = [
    {
      title: '业务类型',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: '分佣比例(%)',
      dataIndex: 'rate',
      key: 'rate',
      render: (text, record) => (
        <span>{record.rate}%</span>
      ),
    },
    {
      title: '最小金额(¥)',
      dataIndex: 'minAmount',
      key: 'minAmount',
      render: (text) => (
        text ? `¥${text}` : '-'
      ),
    },
    {
      title: '最大金额(¥)',
      dataIndex: 'maxAmount',
      key: 'maxAmount',
      render: (text) => (
        text ? `¥${text}` : '无上限'
      ),
    },
    {
      title: '有效期',
      key: 'validity',
      render: (_, record) => {
        if (record.validityType === 'long-term') {
          return <Tag color="green">长期有效</Tag>;
        } else if (record.validityType === 'date-range' && record.validDateRange) {
          return (
            <span>
              {record.validDateRange[0]} 至 {record.validDateRange[1]}
            </span>
          );
        } else if (record.validDays) {
          // 兼容旧数据
          return <span>{record.validDays}天</span>;
        }
        return '-';
      },
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => (
        <Switch checked={record.status} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditRule(record)} style={{ padding: 0 }}>编辑</Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteRule(record.key)} style={{ padding: 0 }}>删除</Button>
        </Space>
      ),
    },
  ];

  // 阶梯奖励规则表格列
  const ladderRulesColumns: ColumnsType<LadderRuleData> = [
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
    },
    {
      title: '销售额下限(¥)',
      dataIndex: 'salesMin',
      key: 'salesMin',
      render: (text) => `¥${text}`,
    },
    {
      title: '销售额上限(¥)',
      dataIndex: 'salesMax',
      key: 'salesMax',
      render: (text) => text === 99999999 ? '无上限' : `¥${text}`,
    },
    {
      title: '奖励比例(%)',
      dataIndex: 'bonusRate',
      key: 'bonusRate',
      render: (text) => `${text}%`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => (
        <Switch checked={record.status} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} style={{ padding: 0 }}>编辑</Button>
          <Button type="link" danger icon={<DeleteOutlined />} style={{ padding: 0 }}>删除</Button>
        </Space>
      ),
    },
  ];

  // 特殊场景分佣表格列 - 删除基础分佣加成列
  const specialScenariosColumns: ColumnsType<SpecialScenarioData> = [
    {
      title: '场景名称',
      dataIndex: 'scenarioName',
      key: 'scenarioName',
    },
    {
      title: '场景类型',
      dataIndex: 'scenarioType',
      key: 'scenarioType',
      render: (text) => {
        const scenarioType = SPECIAL_SCENARIO_TYPES.find(type => type.value === text);
        return scenarioType ? scenarioType.label : text;
      }
    },
    {
      title: '分佣比例(%)',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      render: (text) => `${text}%`,
    },
    {
      title: '有效期',
      key: 'validity',
      render: (_, record) => {
        if (record.validityType === 'long-term') {
          return <Tag color="green">长期有效</Tag>;
        } else if (record.validityType === 'date-range' && record.validDateRange) {
          return (
            <span>
              {record.validDateRange[0]} 至 {record.validDateRange[1]}
            </span>
          );
        } else if (record.validPeriod) {
          // 兼容旧数据
          return <span>{record.validPeriod}天</span>;
        }
        return '-';
      },
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '应用条件',
      dataIndex: 'applyCondition',
      key: 'applyCondition',
      render: (text) => {
        const condition = CONDITION_OPTIONS.find(option => option.value === text);
        return condition ? condition.label : text;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => (
        <Switch checked={record.status} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditScenario(record)} style={{ padding: 0 }}>编辑</Button>
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteScenario(record.key)} style={{ padding: 0 }}>删除</Button>
        </Space>
      ),
    },
  ];

  const handleSaveBasicSettings = (values: any) => {
    console.log('保存基础分佣设置:', values);
    message.success('基础分佣设置已保存');
  };

  const handleSaveValiditySettings = (values: any) => {
    console.log('保存有效期设置:', values);
    message.success('有效期设置已保存');
  };

  const showAddRuleModal = () => {
    addRuleForm.resetFields();
    setAddRuleModalVisible(true);
  };

  const handleCancel = () => {
    setAddRuleModalVisible(false);
  };

  const handleAddRule = async () => {
    try {
      setConfirmLoading(true);
      const values = await addRuleForm.validateFields();
      
      // 创建新规则，确保包含必需的validityType属性
      const newRule: CommissionRuleData = {
        key: Date.now().toString(),
        type: values.type,
        rate: values.rate,
        minAmount: values.minAmount,
        maxAmount: values.maxAmount,
        description: values.description || '',
        status: true,
        validityType: values.validityType // 确保包含此必需属性
      };
      
      // 根据有效期类型添加相应数据
      if (values.validityType === 'date-range' && values.validDateRange) {
        newRule.validDateRange = [
          values.validDateRange[0].format('YYYY-MM-DD'),
          values.validDateRange[1].format('YYYY-MM-DD')
        ];
      }
      
      setBasicRulesList([...basicRulesList, newRule]);
      setAddRuleModalVisible(false);
      message.success('分佣规则添加成功');
    } catch (error) {
      console.error('表单验证失败:', error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleEditRule = (record: CommissionRuleData) => {
    message.info('编辑功能将在后续版本中实现');
  };

  const handleDeleteRule = (key: string) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条分佣规则吗？删除后无法恢复。',
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        setBasicRulesList(basicRulesList.filter(item => item.key !== key));
        message.success('分佣规则已删除');
      }
    });
  };

  const addLadderRule = () => {
    const newRule = {
      key: Date.now().toString(),
      level: '新等级',
      salesMin: 0,
      salesMax: 0,
      bonusRate: 0.5,
      status: true,
    };
    setLadderRulesList([...ladderRulesList, newRule]);
  };

  const showAddScenarioModal = () => {
    addScenarioForm.resetFields();
    setAddScenarioModalVisible(true);
  };
  
  const handleScenarioCancel = () => {
    setAddScenarioModalVisible(false);
  };

  const handleEditScenario = (record: SpecialScenarioData) => {
    message.info('编辑功能将在后续版本中实现');
  };
  
  const handleDeleteScenario = (key: string) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条特殊场景分佣规则吗？删除后无法恢复。',
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        setSpecialScenariosList(specialScenariosList.filter(item => item.key !== key));
        message.success('特殊场景分佣规则已删除');
      }
    });
  };

  const handleScenarioValidityTypeChange = (e: any) => {
    addScenarioForm.setFieldsValue({ validDateRange: undefined });
  };

  const addSpecialScenario = async () => {
    try {
      setScenarioConfirmLoading(true);
      const values = await addScenarioForm.validateFields();
      
      // 创建新的特殊场景规则
      const newScenario: SpecialScenarioData = {
        key: Date.now().toString(),
        scenarioName: values.scenarioName,
        scenarioType: values.scenarioType,
        commissionRate: values.commissionRate,
        validityType: values.validityType,
        priority: values.priority,
        applyCondition: values.applyCondition,
        status: true,
      };
      
      // 根据有效期类型添加相应数据
      if (values.validityType === 'date-range' && values.validDateRange) {
        newScenario.validDateRange = [
          values.validDateRange[0].format('YYYY-MM-DD'),
          values.validDateRange[1].format('YYYY-MM-DD')
        ];
        // 兼容旧数据
        const start = dayjs(values.validDateRange[0]);
        const end = dayjs(values.validDateRange[1]);
        newScenario.validPeriod = end.diff(start, 'day') + 1;
      }
      
      setSpecialScenariosList([...specialScenariosList, newScenario]);
      setAddScenarioModalVisible(false);
      message.success('特殊场景分佣规则添加成功');
    } catch (error) {
      console.error('表单验证失败:', error);
    } finally {
      setScenarioConfirmLoading(false);
    }
  };

  return (
    <div className="commission-config-management">
      <Breadcrumb 
        style={{ marginBottom: 16 }} 
        items={[
          { title: '首页' },
          { title: '分佣配置' },
        ]}
      />
      
      <Tabs defaultActiveKey="1" style={{ background: '#fff', padding: '16px' }}>
        <TabPane tab="基础分佣规则" key="1">
          <Card bordered={false} style={{ marginBottom: 16 }}>
          <Title level={5}>全局分佣设置</Title>
            <Form
              form={basicRulesForm}
              layout="vertical"
              onFinish={handleSaveBasicSettings}
              initialValues={{
                defaultRate: 1,
                minWithdrawal: 100,
              }}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label="默认分佣比例"
                    name="defaultRate"
                    rules={[{ required: true, message: '请输入默认分佣比例' }]}
                  >
                    <InputNumber min={0.1} max={100} step={0.1} style={{ width: '100%' }} addonAfter="%" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="最低提现金额"
                    name="minWithdrawal"
                    rules={[{ required: true, message: '请输入最低提现金额' }]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} addonBefore="¥" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                  保存设置
                </Button>
              </Form.Item>
            </Form>

            <Divider />

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <Title level={5}>分佣规则列表</Title>
              <Button type="primary" icon={<PlusOutlined />} onClick={showAddRuleModal}>
                添加规则
              </Button>
            </div>
            <Table 
              columns={basicRulesColumns} 
              dataSource={basicRulesList} 
              pagination={false}
              rowKey="key"
            />
            
            
            
            
          </Card>
          
          <Modal
            title="添加分佣规则"
            open={addRuleModalVisible}
            onCancel={handleCancel}
            footer={[
              <Button key="back" onClick={handleCancel}>
                取消
              </Button>,
              <Button key="submit" type="primary" loading={confirmLoading} onClick={handleAddRule}>
                保存
              </Button>,
            ]}
            width={700}
          >
            <Form
              form={addRuleForm}
              layout="vertical"
              initialValues={{
                rate: 1,
                validityType: 'date-range',
              }}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="type"
                    label="业务类型"
                    rules={[{ required: true, message: '请选择业务类型' }]}
                  >
                    <Select placeholder="请选择业务类型">
                      {BUSINESS_TYPE_OPTIONS.map(option => (
                        <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="rate"
                    label="分佣比例(%)"
                    rules={[{ required: true, message: '请输入分佣比例' }]}
                  >
                    <InputNumber min={0.1} max={100} step={0.1} style={{ width: '100%' }} addonAfter="%" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="minAmount"
                    label={
                      <span>
                        最小金额(¥)
                        <Tooltip title="客户订单金额必须大于或等于此金额才能触发分佣，低于此金额不参与分佣">
                          <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                        </Tooltip>
                      </span>
                    }
                  >
                    <InputNumber min={1} style={{ width: '100%' }} addonBefore="¥" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="maxAmount"
                    label={
                      <span>
                        最大金额(¥)
                        <Tooltip title="分佣计算的上限金额，超过此金额的部分将不再增加分佣。不填则无上限">
                          <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                        </Tooltip>
                      </span>
                    }
                  >
                    <InputNumber min={1} style={{ width: '100%' }} addonBefore="¥" />
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item 
                name="validityType" 
                label={
                  <span>
                    有效期类型
                    <Tooltip title="选择有效期类型：时间段或长期有效">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                }
                rules={[{ required: true, message: '请选择有效期类型' }]}
              >
                <Radio.Group onChange={handleScenarioValidityTypeChange}>
                  <Radio value="date-range">指定时间段</Radio>
                  <Radio value="long-term">长期有效</Radio>
                </Radio.Group>
              </Form.Item>
              
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.validityType !== currentValues.validityType}
              >
                {({ getFieldValue }) => 
                  getFieldValue('validityType') === 'date-range' ? (
                    <Form.Item
                      name="validDateRange"
                      label="有效时间段"
                      rules={[{ required: true, message: '请选择有效时间段' }]}
                    >
                      <RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
              
              <Form.Item
                name="description"
                label="描述"
              >
                <Input.TextArea rows={3} placeholder="请输入分佣规则描述" />
              </Form.Item>
            </Form>
          </Modal>
        </TabPane>
        
        <TabPane tab="阶梯奖励规则" key="2">
          <Card bordered={false}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <Title level={5}>阶梯奖励规则</Title>
                <Text type="secondary">
                  根据销售额设置不同等级的额外奖励比例，提高员工积极性
                </Text>
              </div>
              <Button type="primary" icon={<PlusOutlined />} onClick={addLadderRule}>
                添加等级
              </Button>
            </div>
            <Table 
              columns={ladderRulesColumns} 
              dataSource={ladderRulesList} 
              pagination={false}
              rowKey="key"
            />
          </Card>
        </TabPane>
        
        <TabPane tab="分佣有效期设置" key="3">
          <Card bordered={false}>
            <Form
              form={validityForm}
              layout="vertical"
              onFinish={handleSaveValiditySettings}
              initialValues={{
                basicValidity: 30,
                extensionCondition: 'repurchase',
                extensionDays: 15,
                maxExtensions: 3,
                enableExtension: true,
              }}
            >
              <Title level={5}>有效期基本设置</Title>
              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item
                    label="基础有效期"
                    name="basicValidity"
                    rules={[{ required: true, message: '请输入基础有效期' }]}
                  >
                    <InputNumber min={1} max={365} style={{ width: '100%' }} addonAfter="天" />
                  </Form.Item>
                </Col>
              </Row>
              
              <Divider />
              
              <Title level={5}>有效期延长设置</Title>
              <Form.Item
                label="启用有效期延长"
                name="enableExtension"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              
              <Form.Item
                label="延长条件"
                name="extensionCondition"
                rules={[{ required: true, message: '请选择延长条件' }]}
              >
                <Select style={{ width: 200 }}>
                  <Select.Option value="repurchase">客户复购</Select.Option>
                  <Select.Option value="recharge">会员充值</Select.Option>
                  <Select.Option value="anyConsumption">任意消费</Select.Option>
                </Select>
              </Form.Item>
              
              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item
                    label="每次延长天数"
                    name="extensionDays"
                    rules={[{ required: true, message: '请输入每次延长天数' }]}
                  >
                    <InputNumber min={1} max={180} style={{ width: '100%' }} addonAfter="天" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="最大延长次数"
                    name="maxExtensions"
                    rules={[{ required: true, message: '请输入最大延长次数' }]}
                  >
                    <InputNumber min={1} max={10} style={{ width: '100%' }} addonAfter="次" />
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                  保存设置
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </TabPane>
        
        <TabPane tab="特殊场景分佣" key="4">
          <Card bordered={false}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <Title level={5}>特殊场景分佣规则</Title>
                <Text type="secondary">
                  为特殊业务场景（如节假日、活动期间）设置差异化分佣比例，优先级高于基础分佣规则
                </Text>
              </div>
              <Button type="primary" icon={<PlusOutlined />} onClick={showAddScenarioModal}>
                添加场景
              </Button>
            </div>
            
            <Alert
              message="特殊场景分佣规则说明"
              description={
                <ul style={{ paddingLeft: 24, marginBottom: 0 }}>
                  <li>特殊场景分佣会覆盖基础分佣规则，按优先级从高到低应用</li>
                  <li>可设置精确的生效时间和应用条件，实现精细化分佣策略</li>
                  <li>适用于节假日、促销活动、会员专享等特殊营销场景</li>
                </ul>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Table 
              columns={specialScenariosColumns} 
              dataSource={specialScenariosList} 
              pagination={false}
              rowKey="key"
            />
          </Card>
          
          <Modal
            title="添加特殊场景分佣规则"
            open={addScenarioModalVisible}
            onCancel={handleScenarioCancel}
            footer={[
              <Button key="back" onClick={handleScenarioCancel}>
                取消
              </Button>,
              <Button key="submit" type="primary" loading={scenarioConfirmLoading} onClick={addSpecialScenario}>
                保存
              </Button>,
            ]}
            width={700}
          >
            <Form
              form={addScenarioForm}
              layout="vertical"
              initialValues={{
                commissionRate: 5,
                validityType: 'date-range',
                priority: 5,
                applyCondition: 'all_orders',
              }}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    name="scenarioName"
                    label="场景名称"
                    rules={[{ required: true, message: '请输入场景名称' }]}
                  >
                    <Input placeholder="例如：国庆节特惠" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="scenarioType"
                    label="场景类型"
                    rules={[{ required: true, message: '请选择场景类型' }]}
                  >
                    <Select placeholder="请选择场景类型">
                      {SPECIAL_SCENARIO_TYPES.map(option => (
                        <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item
                name="commissionRate"
                label={
                  <span>
                    分佣比例(%)
                    <Tooltip title="特殊场景下的总分佣比例，会覆盖基础分佣规则">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                }
                rules={[{ required: true, message: '请输入分佣比例' }]}
              >
                <InputNumber min={0.1} max={100} step={0.1} style={{ width: '100%' }} addonAfter="%" />
              </Form.Item>
              
              <Form.Item
                name="priority"
                label={
                  <span>
                    优先级
                    <Tooltip title="数字越大优先级越高，相同优先级按添加顺序应用">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                }
                rules={[{ required: true, message: '请设置优先级' }]}
              >
                <Slider
                  min={1}
                  max={10}
                  marks={{
                    1: '低',
                    5: '中',
                    10: '高',
                  }}
                />
              </Form.Item>
              
              <Form.Item
                name="applyCondition"
                label={
                  <span>
                    应用条件
                    <Tooltip title="设置此分佣规则适用的客户或订单类型">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                }
                rules={[{ required: true, message: '请选择应用条件' }]}
              >
                <Select placeholder="请选择应用条件">
                  {CONDITION_OPTIONS.map(option => (
                    <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              
              <Form.Item 
                name="validityType" 
                label={
                  <span>
                    有效期类型
                    <Tooltip title="选择有效期类型：时间段或长期有效">
                      <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                }
                rules={[{ required: true, message: '请选择有效期类型' }]}
              >
                <Radio.Group onChange={handleScenarioValidityTypeChange}>
                  <Radio value="date-range">指定时间段</Radio>
                  <Radio value="long-term">长期有效</Radio>
                </Radio.Group>
              </Form.Item>
              
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.validityType !== currentValues.validityType}
              >
                {({ getFieldValue }) => 
                  getFieldValue('validityType') === 'date-range' ? (
                    <Form.Item
                      name="validDateRange"
                      label="有效时间段"
                      rules={[{ required: true, message: '请选择有效时间段' }]}
                    >
                      <RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </Form>
          </Modal>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default CommissionConfigManagement; 